/**
 * clipboard.js — utilitaires copie + partage robustes multi-plateformes
 * (spécial iOS Safari où navigator.clipboard échoue fréquemment)
 */

/**
 * Copie une chaîne dans le presse-papier avec fallback iOS-safe.
 * @param {string} text
 * @returns {Promise<boolean>} true si la copie a réussi
 */
export async function copyToClipboard(text) {
  if (!text) return false;

  // 1) API moderne (HTTPS + gesture requis)
  if (typeof navigator !== "undefined" && navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (_) {
      // continue vers fallback
    }
  }

  // 2) Fallback DOM (iOS Safari nécessite contentEditable + Range/Selection)
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    // Éviter le zoom iOS et rendre le textarea invisible sans display:none
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.width = "1px";
    ta.style.height = "1px";
    ta.style.padding = "0";
    ta.style.border = "0";
    ta.style.outline = "0";
    ta.style.boxShadow = "none";
    ta.style.background = "transparent";
    ta.style.fontSize = "16px"; // évite le zoom sur iOS
    document.body.appendChild(ta);

    // iOS a besoin d'une sélection via Range
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      ta.contentEditable = "true";
      ta.readOnly = false;
      const range = document.createRange();
      range.selectNodeContents(ta);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      ta.setSelectionRange(0, text.length);
    } else {
      ta.select();
    }

    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (_) {
    return false;
  }
}

/**
 * Détecte si l'API Web Share native est disponible (mobile principalement).
 */
export function canWebShare(data = {}) {
  if (typeof navigator === "undefined" || !navigator.share) return false;
  if (navigator.canShare && Object.keys(data).length) {
    try { return navigator.canShare(data); } catch (_) { return true; }
  }
  return true;
}

/**
 * Partage via l'API Web Share si dispo. Ne lève pas d'erreur si l'utilisateur annule.
 * @param {{title?:string, text?:string, url?:string}} data
 * @returns {Promise<'shared'|'cancelled'|'unsupported'>}
 */
export async function nativeShare(data) {
  if (!canWebShare(data)) return "unsupported";
  try {
    await navigator.share(data);
    return "shared";
  } catch (e) {
    // AbortError = utilisateur a annulé, pas une erreur métier
    if (e && (e.name === "AbortError" || /abort/i.test(String(e.message)))) return "cancelled";
    throw e;
  }
}
